import React, { useState, useEffect } from 'react';
import { api } from '../../services/api.js';
import { JurnalAkuntansi } from '../../types/index.js';
import { Search, FileText, Download, Calendar } from 'lucide-react';
import { SortableHeader } from '../ui/SortableHeader.js';

export const JurnalAkuntansiView: React.FC = () => {
  const [jurnal, setJurnal] = useState<JurnalAkuntansi[]>([]);
  const [loading, setLoading] = useState(true);
  
  const [startDate, setStartDate] = useState(
    new Date(new Date().setDate(1)).toISOString().slice(0, 10) // First day of current month
  );
  const [endDate, setEndDate] = useState(
    new Date().toISOString().slice(0, 10) // Today
  );
  const [keyword, setKeyword] = useState('');
  
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [summary, setSummary] = useState({ totalDebit: 0, totalKredit: 0 });

  const fetchJurnal = async () => {
    setLoading(true);
    try {
      const res = await api.getJurnal({
        startDate,
        endDate,
        keyword,
        page,
        limit: 50
      });
      
      setJurnal(res.data);
      setTotalPages(res.totalPages || 1);
      setSummary(res.summary);
      
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(fetchJurnal, 300);
    return () => clearTimeout(timer);
  }, [startDate, endDate, keyword, page]);

  return (
    <div className="space-y-4 animate-in fade-in duration-200">
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <FileText className="w-5 h-5 text-sky-600" />
            Jurnal Umum Akuntansi
          </h2>
          <p className="text-xs text-slate-500">
            Riwayat transaksi yang tercatat di pembukuan (Double-Entry).
          </p>
        </div>
        <div className="flex gap-2">
          <button className="px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 transition shadow-xs flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export Excel
          </button>
        </div>
      </div>

      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
        <div className="flex flex-col md:flex-row gap-4 mb-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Cari referensi, keterangan, atau akun..."
                value={keyword}
                onChange={(e) => {
                  setKeyword(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:ring-1 focus:ring-sky-500"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-400" />
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
            />
            <span className="text-slate-400">s/d</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
            />
          </div>
        </div>

        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full text-xs text-left">
            <thead className="bg-slate-100 text-slate-600 font-bold uppercase tracking-wider">
              <tr>
                <th className="py-3 px-4 w-32">Tanggal</th>
                <th className="py-3 px-4 w-40">Referensi</th>
                <th className="py-3 px-4">Keterangan</th>
                <th className="py-3 px-4 w-48">Akun</th>
                <th className="py-3 px-4 w-32 text-right">Debit</th>
                <th className="py-3 px-4 w-32 text-right">Kredit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-10 text-slate-400">Memuat jurnal...</td>
                </tr>
              ) : jurnal.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-10 text-slate-400">Tidak ada data jurnal.</td>
                </tr>
              ) : (
                jurnal.map((j) => (
                  <tr key={j.id} className="hover:bg-slate-50 transition">
                    <td className="py-2.5 px-4 font-mono text-slate-600">
                      {new Date(j.tanggal).toLocaleDateString('id-ID', {
                        day: '2-digit', month: '2-digit', year: 'numeric'
                      })}
                    </td>
                    <td className="py-2.5 px-4 font-mono text-slate-700">{j.no_referensi}</td>
                    <td className="py-2.5 px-4 text-slate-700">{j.keterangan}</td>
                    <td className="py-2.5 px-4 font-semibold text-slate-700">
                      {j.debit === 0 ? (
                        <span className="pl-4 text-slate-600">{j.nama_akun}</span>
                      ) : (
                        j.nama_akun
                      )}
                    </td>
                    <td className="py-2.5 px-4 text-right font-mono font-bold text-slate-800">
                      {j.debit > 0 ? j.debit.toLocaleString('id-ID') : '-'}
                    </td>
                    <td className="py-2.5 px-4 text-right font-mono font-bold text-slate-800">
                      {j.kredit > 0 ? j.kredit.toLocaleString('id-ID') : '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {!loading && jurnal.length > 0 && (
              <tfoot className="bg-slate-50 font-bold border-t-2 border-slate-200">
                <tr>
                  <td colSpan={4} className="py-3 px-4 text-right text-slate-700">TOTAL PERIODE:</td>
                  <td className="py-3 px-4 text-right font-mono text-slate-900">{summary.totalDebit.toLocaleString('id-ID')}</td>
                  <td className="py-3 px-4 text-right font-mono text-slate-900">{summary.totalKredit.toLocaleString('id-ID')}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between">
            <div className="text-xs text-slate-500">
              Halaman {page} dari {totalPages}
            </div>
            <div className="flex gap-1">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 text-xs font-semibold rounded bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
              >
                Prev
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1.5 text-xs font-semibold rounded bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
